import { ContainerGutter, ContainerPadding, FilterTag } from '@volkswagen-onehub/components-core';
import gsap from 'gsap';
import React, { useLayoutEffect, useRef } from 'react';
import { Carline, SalesgroupWithCarline } from '../../../../../types/vehicle-types';
import { LoadingScreen } from '../../../../screen/loading';
import { useVehicleFilter } from './hooks/use-vehicle-filter';
import { FilterTagsContainer, VehiclesGridWrapper, ViewsContainer, ViewWrapper } from './ui/vehicle-grid-styles';
import { VehicleCardRadio } from './vehicle-card-radio';
import { VehicleCardSelected } from './vehicle-card-selected';

interface Props {
	label?: string;
	required?: boolean;
	carlines?: Carline[];
	salesgroups?: SalesgroupWithCarline[];
}

export function SelectorView(props: Props) {
	const {
		activeFilter,
		activeIndex,
		hasError,
		motorizacionFilters,
		options,
		isCarlineView,
		changeView,
		handleFilterClick,
		handleVehicleSelection,
	} = useVehicleFilter({ carlines: props.carlines, salesgroups: props.salesgroups });

	const viewsContainerRef = useRef<HTMLDivElement>(null);
	const gridViewRef = useRef<HTMLDivElement>(null);
	const carlineViewRef = useRef<HTMLDivElement>(null);
	const vehiclesGridRef = useRef<HTMLDivElement>(null);
	const isFirstRenderRef = useRef(true);
	const hasOptions = Boolean(options && options.length > 0);

	// transición de opacidad al cambiar de filtro (grid <-> grid)
	useLayoutEffect(() => {
		if (isCarlineView || !vehiclesGridRef.current) return;
		gsap.fromTo(vehiclesGridRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power1.in' });
	}, [activeFilter, isCarlineView]);

	// transición de opacidad y altura al cambiar de vista (grid <-> carline)
	useLayoutEffect(() => {
		const activeRef = isCarlineView ? carlineViewRef.current : gridViewRef.current;
		const inactiveRef = isCarlineView ? gridViewRef.current : carlineViewRef.current;
		const container = viewsContainerRef.current;
		if (!activeRef || !inactiveRef || !container) return;

		const targetHeight = activeRef.scrollHeight;

		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			gsap.set(activeRef, { opacity: 1 });
			gsap.set(inactiveRef, { opacity: 0 });
		} else {
			const tl = gsap.timeline();
			tl.to(inactiveRef, { opacity: 0, duration: 0.3, ease: 'power1.inOut' }, 0)
				.to(container, { height: targetHeight, duration: 0.3, ease: 'power1.inOut' }, 0)
				.to(activeRef, { opacity: 1, duration: 0.3, ease: 'power1.inOut' }, 0.1);
		}

		// recalcula la altura si el contenido de la vista activa cambia
		const resizeObserver = new ResizeObserver(() => {
			gsap.to(container, { height: activeRef.scrollHeight, duration: 0.3, ease: 'power1.inOut', overwrite: 'auto' });
		});
		resizeObserver.observe(activeRef);

		return () => {
			resizeObserver.disconnect();
		};
	}, [isCarlineView, options]);

	// scroll (focus) al carline-view cuando cambiamos de grid-view -> carline-view
	useLayoutEffect(() => {
		if (!isCarlineView || !carlineViewRef.current) return;
		carlineViewRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
	}, [isCarlineView]);

	if (!hasOptions) {
		return <LoadingScreen />;
	}

	return (
		<ViewsContainer ref={viewsContainerRef}>
			<ViewWrapper ref={gridViewRef} $isActive={!isCarlineView} id="grid-view">
				{motorizacionFilters.length > 1 && (
					<FilterTagsContainer
						data-testid="filter-tags-container"
						gutter={ContainerGutter.static150}
						padding={{
							top: ContainerPadding.static200,
							bottom: ContainerPadding.static400,
						}}
						wrap={'never'}
					>
						<FilterTag
							emphasis="secondary"
							text="Todos"
							active={activeFilter === null}
							onClick={() => handleFilterClick(null)}
							aria-label="Filtrar por todos los vehículos"
						/>
						{motorizacionFilters.map((filter) => (
							<FilterTag
								key={filter.key}
								emphasis="secondary"
								text={filter.label}
								active={activeFilter === filter.key}
								onClick={() => handleFilterClick(filter.key)}
								aria-label={`Filtrar por ${filter.label}`}
							/>
						))}
					</FilterTagsContainer>
				)}

				<VehiclesGridWrapper ref={vehiclesGridRef} id="vehicles-grid-wrapper">
					{options.map((option, index) => (
						<VehicleCardRadio
							key={`${activeFilter ?? 'all'}-${index}`}
							option={option}
							isActive={activeIndex === index}
							hasError={hasError}
							onSelect={(e: React.MouseEvent) => handleVehicleSelection(e, option, index)}
							aria-label={`Seleccionar vehículo ${option.title}`}
						/>
					))}
				</VehiclesGridWrapper>
			</ViewWrapper>

			<ViewWrapper ref={carlineViewRef} $isActive={isCarlineView} id="carline-view">
				<VehicleCardSelected option={options[activeIndex ?? 0]} onSetGridView={changeView} />
			</ViewWrapper>
		</ViewsContainer>
	);
}
